import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth';
import { toast } from 'sonner';
import { UserRole } from '@/types';
import { UserRole as UserRoleEnum } from '@/types/enums';
import { adminPasswordResetService } from '@/services/adminPasswordResetService';
import { EmployeeStatus } from '@/contexts/employees/EmployeesContext';

// Type definition for User (system user from profiles table)
export interface SystemUser extends Record<string, unknown> {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  department: string | string[];
  position: string;
  status: string;
  company_id?: string;
  created_at?: string;
  updated_at?: string;
}

export function useUserManagement() {
  const { user: currentUser } = useAuth();
  
  // State management
  const [activeTab, setActiveTab] = useState("users");
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isChangeDeptDialogOpen, setIsChangeDeptDialogOpen] = useState(false);
  const [isChangeStatusDialogOpen, setIsChangeStatusDialogOpen] = useState(false);
  const [isPasswordResetDialogOpen, setIsPasswordResetDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<null | SystemUser>(null);

  const [users, setUsers] = useState<SystemUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch users from profiles table
  const fetchUsers = useCallback(async () => {
    if (!currentUser?.companyId) return;
    
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('company_id', currentUser.companyId)
        .order('name');
      
      if (error) throw error;
      
      const transformedUsers: SystemUser[] = (data || []).map((profile: any) => {
        // Keep department as array for multi-select compatibility
        const userDepartment = Array.isArray(profile.department) 
          ? profile.department 
          : (profile.department ? [profile.department] : []);

        return {
          id: profile.id,
          name: profile.name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unknown',
          email: profile.email || '',
          role: profile.role as UserRole,
          department: userDepartment,
          position: profile.position || '',
          status: profile.status || EmployeeStatus.ACTIVE,
          company_id: profile.company_id,
          created_at: profile.created_at,
          updated_at: profile.updated_at
        };
      });
      
      setUsers(transformedUsers);
    } catch {
      toast.error('Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.companyId]);

  // Fetch users on mount and when currentUser changes
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // User action handlers
  const viewUserProfile = useCallback((user: SystemUser) => {
    setSelectedUser(user);
    setIsProfileDialogOpen(true);
  }, []);

  const handleEditUser = useCallback((user: SystemUser) => {
    setSelectedUser(user);
    setIsEditDialogOpen(true);
  }, []);

  const openChangeDepartmentDialog = useCallback((user: SystemUser) => {
    setSelectedUser(user);
    setIsChangeDeptDialogOpen(true);
  }, []);

  const openChangeStatusDialog = useCallback((user: SystemUser) => {
    setSelectedUser(user);
    setIsChangeStatusDialogOpen(true);
  }, []);

  const handlePasswordReset = useCallback((user: SystemUser) => {
    setSelectedUser(user);
    setIsPasswordResetDialogOpen(true);
  }, []);

  const onResetPassword = useCallback(async (userId: string, newPassword: string): Promise<void> => {
    await adminPasswordResetService.resetUserPassword(userId, newPassword);
  }, []);

  // Function that matches the component signature expectation
  const onResetPasswordFromUser = useCallback((user: SystemUser) => {
    handlePasswordReset(user);
  }, [handlePasswordReset]);

  // Data update handlers
  const saveEditedUser = async (updatedUser: SystemUser) => {
    try {
      
      // Handle department array for database storage
      const departmentArray = Array.isArray(updatedUser.department) 
        ? updatedUser.department 
        : (updatedUser.department ? [updatedUser.department] : []);
      
      const updateData = {
        name: updatedUser.name,
        email: updatedUser.email,
        role: updatedUser.role,
        department: departmentArray,
        position: updatedUser.position,
        status: updatedUser.status,
        updated_at: new Date().toISOString()
      };
      
      const { error } = await (supabase as any)
        .from('profiles')
        .update(updateData)
        .eq('id', updatedUser.id);
      
      if (error) throw error;
      
      // Refresh the users list
      await fetchUsers();
      setIsEditDialogOpen(false);
      toast.success("User information updated successfully");
    } catch {
      toast.error('Failed to update user information');
    }
  };

  const saveDepartmentChange = async (userId: string, newDepartment: string) => {
    try {
      
      // Check if the user is a department head
      const user = users.find(u => u.id === userId);
      if (!user || user.role !== UserRoleEnum.DEPARTMENT_HEAD) {
        toast.error('Only department heads can have departments assigned');
        return;
      }
      
      const displayDepartment = newDepartment === "" ? "Unassigned" : newDepartment;
      
      const updateData = { 
        department: newDepartment,
        updated_at: new Date().toISOString()
      };
      
      const { error } = await (supabase as any)
        .from('profiles')
        .update(updateData)
        .eq('id', userId);
        
      if (error) throw error;
      
      await fetchUsers();
      setIsChangeDeptDialogOpen(false);
      toast.success(`Department changed to ${displayDepartment} successfully`);
    } catch {
      toast.error('Failed to change department');
    }
  };

  const saveStatusChange = async (userId: string, newStatus: string) => {
    try {
      
      const updateData = { 
        status: newStatus,
        updated_at: new Date().toISOString()
      };
      
      const { error } = await (supabase as any)
        .from('profiles')
        .update(updateData)
        .eq('id', userId)
        .select('*');
        
      if (error) {
        throw error;
      }
      
      
      await fetchUsers();
      setIsChangeStatusDialogOpen(false);
      toast.success(`Status changed to ${newStatus} successfully`);
    } catch {
      toast.error('Failed to change status');
    }
  };

  // Dialog close handlers
  const handleProfileDialogClose = (open: boolean) => {
    setIsProfileDialogOpen(open);
    if (!open) {
      setSelectedUser(null);
    }
  };

  const handleEditDialogClose = (open: boolean) => {
    setIsEditDialogOpen(open);
    if (!open) {
      setSelectedUser(null);
    }
  };

  const handleChangeDeptDialogClose = (open: boolean) => {
    setIsChangeDeptDialogOpen(open);
    if (!open) {
      setSelectedUser(null);
    }
  };

  const handleChangeStatusDialogClose = (open: boolean) => {
    setIsChangeStatusDialogOpen(open);
    if (!open) {
      setSelectedUser(null);
    }
  };

  const handlePasswordResetDialogClose = (open: boolean) => {
    setIsPasswordResetDialogOpen(open);
    if (!open) {
      setSelectedUser(null);
    }
  };





  return {
    activeTab,
    setActiveTab,
    users,
    isLoading,
    isProfileDialogOpen,
    isEditDialogOpen,
    isChangeDeptDialogOpen,
    isChangeStatusDialogOpen,
    isPasswordResetDialogOpen,
    selectedUser,
    viewUserProfile,
    handleEditUser,
    openChangeDepartmentDialog,
    openChangeStatusDialog,
    handlePasswordReset,
    onResetPassword: onResetPasswordFromUser, // Export the correct signature
    onResetPasswordInternal: onResetPassword, // Keep the original for dialog use
    saveEditedUser,
    saveDepartmentChange,
    saveStatusChange,
    handleProfileDialogClose,
    handleEditDialogClose,
    handleChangeDeptDialogClose,
    handleChangeStatusDialogClose,
    handlePasswordResetDialogClose,
    fetchUsers
  };
} 